import { Banner, DataField, StrongValue } from '@if/mui'
import { Grid, Skeleton, Stack } from '@mui/material'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

export interface StageField {
  label: string
  // Empty value (null/undefined/'') shows the em-dash placeholder.
  value: ReactNode
}

interface Props {
  // Label/value rows rendered in a responsive grid.
  fields?: StageField[]
  // Number of field columns on sm+ screens (default 2).
  columns?: 2 | 3
  // Buttons rendered in a wrapping row below the fields.
  actions?: ReactNode
  // Step-specific block (simulator, alerts, applicant sections…) rendered above the fields.
  customContent?: ReactNode
  // This step's data hasn't been computed yet — show the skeleton in place of the content.
  loading?: boolean
  // The async checks still owe a result — show the in-progress banner. Outlives `loading`:
  // the values can land while the remaining checks are still running.
  pendingChecks?: boolean
}

// Generic stage body reused by every step — supply the data, it owns the layout.
const StageContent = ({ fields, columns = 2, actions, customContent, loading, pendingChecks }: Props) => {
  const { t } = useTranslation()

  // While the data is still being computed the step's own banners and actions stay out —
  // they would report a state that isn't decided yet.
  const body = loading ? (
    <Stack spacing={1.5}>
      <Skeleton variant="rounded" width="45%" height={20} />
      <Skeleton variant="rounded" width="70%" height={20} />
      <Skeleton variant="rounded" width="55%" height={20} />
    </Stack>
  ) : (
    <>
      {customContent}
      {fields && fields.length > 0 && (
        <Grid container spacing={2}>
          {fields.map((field) => (
            <Grid key={field.label} size={{ xs: 12, sm: 12 / columns }}>
              <DataField
                label={field.label}
                value={field.value != null && field.value !== '' ? <StrongValue>{field.value}</StrongValue> : undefined}
              />
            </Grid>
          ))}
        </Grid>
      )}
      {actions && (
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {actions}
        </Stack>
      )}
    </>
  )

  return (
    <Stack spacing={2}>
      {pendingChecks && (
        <Banner severity="info" title={t('viewCreditApplication.stageBanner.inProgressTitle')}>
          {t('viewCreditApplication.stageBanner.inProgressMessage')}
        </Banner>
      )}
      {body}
    </Stack>
  )
}

export default StageContent
